import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Form, Button, Badge, Spinner, Alert } from 'react-bootstrap';

import coldemailer_api from './coldemailer_api';
import coldemailer_config from './coldemailer_config';
import CreateEmailCampaignModal from './modals/CreateEmailCampaignModal';
import PreviewEmailModal from './modals/PreviewEmailModal';
import OutreachRecipientTable from './components/OutreachRecipientTable';
import EmailLists from './components/EmailLists';
import TestModeBanner from './components/TestModeBanner';
import './coldemailer.css';
import './coldemailer-styles.css';

const c_RowsPerPageOptions = [10, 25, 50, 100];
const c_DefaultRowsPerPage = 25;

// One address takes a provider call plus the throttle, so a couple of seconds between polls is
// enough to watch the count come down without asking constantly
const c_VerificationPollMs = 2000;

const c_BadgeVariants = {
	active: 'success',
	inactive: 'secondary',
	paused: 'danger',
	archived: 'dark'
};

const format_date = (value) => {
	if(!value) {
		return '-';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '-';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const get_badge_variant = (status) => {
	if(status && c_BadgeVariants[status]) {
		return c_BadgeVariants[status];
	}

	return 'secondary';
};

function ColdEmailerView() {
	const navigate = useNavigate();

	const [email_campaigns, set_email_campaigns] = useState([]);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');
	const [search_text, set_search_text] = useState('');
	const [page, set_page] = useState(0);
	const [rows_per_page, set_rows_per_page] = useState(c_DefaultRowsPerPage);
	const [show_create_modal, set_show_create_modal] = useState(false);
	const [show_preview_modal, set_show_preview_modal] = useState(false);
	const [test_status_version, set_test_status_version] = useState(0);

	// Addresses currently waiting on the verification provider, and whatever went wrong asking.
	// The error is held separately so a failing poll says so rather than silently reading as zero.
	const [verifying_count, set_verifying_count] = useState(0);
	const [verification_error, set_verification_error] = useState('');

	const on_preview_email = () => {
		set_show_preview_modal(true);
	};

	const on_close_preview_modal = () => {
		set_show_preview_modal(false);
	};

	const load_email_campaigns = async () => {
		set_loading(true);
		set_error('');

		try {
			const campaigns = await coldemailer_api.get_email_campaigns();
			set_email_campaigns(campaigns);
		} catch (error) {
			console.log('ColdEmailerView.load_email_campaigns - ERROR: ', error);
			set_error('Failed to load email campaigns.');
		} finally {
			set_loading(false);
		}
	};

	useEffect(function() {
		load_email_campaigns();
	}, []);

	// Polled while this screen is open rather than only after an add started here, because the run
	// belongs to the service: it can have been started from the list screen, or be still going from
	// before this page was opened.
	useEffect(function() {
		var stopped = false;

		const read_progress = async () => {
			try {
				const progress = await coldemailer_api.get_verification_progress();

				if(stopped) {
					return;
				}

				set_verifying_count(progress && progress.in_progress ? progress.in_progress : 0);
				set_verification_error('');
			} catch (error) {
				console.log('ColdEmailerView.read_progress - ERROR: ', error);

				if(stopped) {
					return;
				}

				// Said out loud rather than left as a stale count: a poll that cannot answer is not
				// the same as nothing being verified.
				set_verification_error('Could not read verification progress.');
			}
		};

		read_progress();

		const timer = setInterval(read_progress, c_VerificationPollMs);

		return function() {
			stopped = true;
			clearInterval(timer);
		};
	}, []);

	const matches_search = (campaign) => {
		if(!search_text) {
			return true;
		}

		const needle = search_text.toLowerCase();
		const name = campaign.name ? campaign.name.toLowerCase() : '';
		const description = campaign.description ? campaign.description.toLowerCase() : '';

		return name.indexOf(needle) !== -1 || description.indexOf(needle) !== -1;
	};

	const filtered_campaigns = email_campaigns.filter(matches_search);
	const total_pages = Math.max(1, Math.ceil(filtered_campaigns.length / rows_per_page));
	const current_page = Math.min(page, total_pages - 1);
	const first_row = current_page * rows_per_page;
	const visible_campaigns = filtered_campaigns.slice(first_row, first_row + rows_per_page);

	const on_search_change = (event) => {
		set_search_text(event.target.value);
		set_page(0);
	};

	const on_rows_per_page_change = (event) => {
		set_rows_per_page(parseInt(event.target.value));
		set_page(0);
	};

	const on_previous_page = () => {
		if(current_page > 0) {
			set_page(current_page - 1);
		}
	};

	const on_next_page = () => {
		if(current_page < total_pages - 1) {
			set_page(current_page + 1);
		}
	};

	const on_row_click = (campaign) => {
		navigate(coldemailer_config.campaign_route + '/' + campaign.id);
	};

	// Bumped when the mode changes, so the recipients table re-reads it and shows or hides
	// its test only controls without a page reload. Switching mode also pauses or resumes
	// campaigns, so the list is reloaded to show their new status.
	const on_test_status_change = async () => {
		set_test_status_version(function(previous) {
			return previous + 1;
		});

		await load_email_campaigns();
	};

	const on_create_campaign = () => {
		set_show_create_modal(true);
	};

	const on_hide_create_modal = () => {
		set_show_create_modal(false);
	};

	// Reloads from the server so the table shows exactly what was stored
	const on_campaign_created = async () => {
		set_page(0);
		await load_email_campaigns();
	};

	// Only says something when there is something to say: a run in progress, or a poll that could
	// not answer. Silent the rest of the time.
	const render_verification_note = () => {
		if(verification_error) {
			return (
				<p className="ce-verification-note ce-verification-note-error">
					{verification_error}
				</p>
			);
		}

		if(verifying_count === 0) {
			return null;
		}

		const address_word = verifying_count === 1 ? ' address' : ' addresses';

		return (
			<p className="ce-verification-note">
				{'Verifying ' + verifying_count + address_word + '. Recipients appear as each one is confirmed.'}
			</p>
		);
	};

	const render_header = () => {
		return (
			<div className="ce-header">
				<div>
					<h4 className="ce-title">Cold Emailer</h4>
					<p className="ce-subtitle">Manage outreach campaigns and their emails</p>

					{render_verification_note()}
				</div>

				<div className="ce-header-actions">
					<Button variant="outline-light" size="sm" className="ce-button" onClick={load_email_campaigns}>
						Refresh
					</Button>

					<Button variant="outline-light" size="sm" className="ce-button" onClick={on_preview_email}>
						Test Email
					</Button>

					<Button size="sm" className="ce-button-create" onClick={on_create_campaign}>
						Create Campaign
					</Button>
				</div>
			</div>
		);
	};

	const render_error = () => {
		return (
			<Alert variant="danger">
				{error}
			</Alert>
		);
	};

	const render_loading = () => {
		return (
			<div className="ce-loading">
				<Spinner animation="border" size="sm" />
			</div>
		);
	};

	const render_toolbar = () => {
		return (
			<div className="ce-toolbar">
				<Form.Group className="ce-field ce-field-search">
					<Form.Label className="ce-label">Search</Form.Label>
					<Form.Control
						type="text"
						placeholder="Filter by name or description"
						value={search_text}
						onChange={on_search_change}
					/>
				</Form.Group>

				<Form.Group className="ce-field ce-field-rows">
					<Form.Label className="ce-label">Rows</Form.Label>
					<Form.Select value={rows_per_page} onChange={on_rows_per_page_change}>
						{c_RowsPerPageOptions.map(function(option) {
							return (
								<option key={option} value={option}>
									{option}
								</option>
							);
						})}
					</Form.Select>
				</Form.Group>

				<div className="ce-spacer"></div>
			</div>
		);
	};

	const render_empty_row = () => {
		return (
			<tr>
				<td colSpan={5} className="ce-empty">
					{email_campaigns.length === 0 ? 'No email campaigns yet.' : 'No campaigns match your search.'}
				</td>
			</tr>
		);
	};

	const render_row = (campaign) => {
		return (
			<tr key={campaign.id} className="ce-row-clickable" onClick={function() { on_row_click(campaign); }}>
				<td className="ce-col-name">
					{campaign.name ? campaign.name : '-'}
				</td>
				<td className="ce-col-description">
					{campaign.description ? campaign.description : '-'}
				</td>
				<td className="ce-col-status">
					<Badge bg={get_badge_variant(campaign.status)}>
						{campaign.status ? campaign.status : 'unknown'}
					</Badge>
				</td>
				<td className="ce-col-landing">
					{campaign.landing_page_base_url ? campaign.landing_page_base_url : '-'}
				</td>
				<td className="ce-col-created">
					{format_date(campaign.createdAt)}
				</td>
			</tr>
		);
	};

	const render_table = () => {
		return (
			<Table striped bordered hover responsive variant="dark" className="ce-table">
				<thead>
					<tr>
						<th className="ce-col-name">Name</th>
						<th className="ce-col-description">Description</th>
						<th className="ce-col-status">Status</th>
						<th className="ce-col-landing">Landing Page</th>
						<th className="ce-col-created">Created</th>
					</tr>
				</thead>
				<tbody>
					{visible_campaigns.length === 0 ? render_empty_row() : visible_campaigns.map(render_row)}
				</tbody>
			</Table>
		);
	};

	const render_pagination = () => {
		const last_row = Math.min(first_row + rows_per_page, filtered_campaigns.length);
		const showing_from = filtered_campaigns.length === 0 ? 0 : first_row + 1;

		return (
			<div className="ce-pagination">
				<div className="ce-pagination-info">
					{'Showing ' + showing_from + ' to ' + last_row + ' of ' + filtered_campaigns.length}
				</div>

				<div className="ce-pagination-controls">
					<Button
						variant="outline-light"
						size="sm" className="ce-button"
						disabled={current_page === 0}
						onClick={on_previous_page}
					>
						Previous
					</Button>

					<span className="ce-page-label">
						{'Page ' + (current_page + 1) + ' of ' + total_pages}
					</span>

					<Button
						variant="outline-light"
						size="sm" className="ce-button"
						disabled={current_page >= total_pages - 1}
						onClick={on_next_page}
					>
						Next
					</Button>
				</div>
			</div>
		);
	};

	const render_panel = () => {
		return (
			<div className="ce-panel">
				{render_toolbar()}
				{render_table()}
				{render_pagination()}
			</div>
		);
	};

	// Set as a custom property so the host app's header height stays configurable
	const root_style = { '--ce-top-offset': coldemailer_config.top_offset_px + 'px' };

	return (
		<div className="ce-root" style={root_style}>
			<TestModeBanner on_status_change={on_test_status_change} />

			{render_header()}

			{error ? render_error() : null}
			{loading ? render_loading() : render_panel()}

			<EmailLists />

			<OutreachRecipientTable test_status_version={test_status_version} />

			<CreateEmailCampaignModal
				show={show_create_modal}
				on_hide={on_hide_create_modal}
				on_created={on_campaign_created}
			/>

			<PreviewEmailModal
				show={show_preview_modal}
				on_close={on_close_preview_modal}
			/>
		</div>
	);
}

export default ColdEmailerView;
